import type OlMap from 'ol/Map'
import type { Basemap } from './mapInit'

/**
 * PNG export (PRD P1.5): composite the rendered OL layer canvases into one
 * image with the scale bar and attribution burned in, then download it.
 * Basemap sources load with crossOrigin:'anonymous' so the canvas stays
 * untainted; if a taint SecurityError still occurs (e.g. a future basemap
 * without CORS), the export retries once without the basemap and still
 * delivers the WMS/vector content.
 */
export async function exportMapPng(map: OlMap, basemaps: Basemap[], filename: string): Promise<void> {
  try {
    const blob = await renderToBlob(map)
    download(blob, filename)
  } catch (err) {
    if (!isSecurityError(err)) throw err
    console.warn('Export canvas tainted by the basemap — retrying without it (P1.5 fallback).')
    const visibilities = basemaps.map((bm) => bm.layer?.getVisible() ?? false)
    basemaps.forEach((bm) => bm.layer?.setVisible(false))
    try {
      const blob = await renderToBlob(map)
      download(blob, filename)
    } finally {
      basemaps.forEach((bm, i) => bm.layer?.setVisible(visibilities[i]))
    }
  }
}

function isSecurityError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'SecurityError'
}

function renderToBlob(map: OlMap): Promise<Blob> {
  return new Promise((resolve, reject) => {
    map.once('rendercomplete', () => {
      try {
        const size = map.getSize()
        if (!size) throw new Error('map has no size')
        const canvas = document.createElement('canvas')
        canvas.width = size[0]
        canvas.height = size[1]
        const ctx = canvas.getContext('2d')
        if (!ctx) throw new Error('no 2d context')
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        // Standard OL export recipe: draw each layer canvas with its CSS
        // transform and opacity applied.
        const target = map.getTargetElement()
        target.querySelectorAll<HTMLCanvasElement>('.ol-layer canvas, canvas.ol-layer').forEach((layerCanvas) => {
          if (layerCanvas.width === 0) return
          const parent = layerCanvas.parentNode as HTMLElement | null
          const opacity = parent?.style.opacity || layerCanvas.style.opacity
          ctx.globalAlpha = opacity === '' ? 1 : Number(opacity)
          const transform = layerCanvas.style.transform
          const matrix = transform.match(/^matrix\(([^(]*)\)$/)?.[1].split(',').map(Number)
          if (matrix && matrix.length === 6) {
            ctx.setTransform(matrix[0], matrix[1], matrix[2], matrix[3], matrix[4], matrix[5])
          } else {
            ctx.setTransform(1, 0, 0, 1, 0, 0)
          }
          ctx.drawImage(layerCanvas, 0, 0)
        })
        ctx.setTransform(1, 0, 0, 1, 0, 0)
        ctx.globalAlpha = 1

        burnScaleBar(ctx, target, canvas.height)
        burnAttribution(ctx, target, canvas)

        // toBlob throws SecurityError synchronously when the canvas is tainted.
        canvas.toBlob((blob) => {
          if (blob) resolve(blob)
          else reject(new Error('canvas.toBlob produced no data'))
        }, 'image/png')
      } catch (err) {
        reject(err)
      }
    })
    map.renderSync()
  })
}

/** Redraw the live ol-scale-line as a simple bar + label, bottom-left. */
function burnScaleBar(ctx: CanvasRenderingContext2D, target: HTMLElement, height: number): void {
  const inner = target.querySelector<HTMLElement>('.ol-scale-line-inner')
  if (!inner) return
  const width = Math.round(inner.getBoundingClientRect().width)
  const label = inner.textContent ?? ''
  if (!width || !label) return
  const x = 12
  const y = height - 18
  ctx.fillStyle = 'rgba(255, 255, 255, 0.75)'
  ctx.fillRect(x - 4, y - 16, width + 8, 24)
  ctx.strokeStyle = '#333'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(x, y - 4)
  ctx.lineTo(x, y)
  ctx.lineTo(x + width, y)
  ctx.lineTo(x + width, y - 4)
  ctx.stroke()
  ctx.fillStyle = '#333'
  ctx.font = '11px system-ui, sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText(label, x + width / 2, y - 5)
}

/** Burn the attribution control's current text, bottom-right. */
function burnAttribution(ctx: CanvasRenderingContext2D, target: HTMLElement, canvas: HTMLCanvasElement): void {
  const items = [...target.querySelectorAll<HTMLElement>('.ol-attribution li')]
    .map((li) => li.textContent?.trim() ?? '')
    .filter((t) => t !== '')
  if (items.length === 0) return
  const text = items.join(' · ')
  ctx.font = '10px system-ui, sans-serif'
  ctx.textAlign = 'right'
  const metrics = ctx.measureText(text)
  const x = canvas.width - 8
  const y = canvas.height - 8
  ctx.fillStyle = 'rgba(255, 255, 255, 0.75)'
  ctx.fillRect(x - metrics.width - 6, y - 12, metrics.width + 12, 18)
  ctx.fillStyle = '#333'
  ctx.fillText(text, x, y)
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
